import { useCallback, useMemo, useRef } from 'react';

import CustomButton from '../../common/CustomButton/CustomButton';
import CustomLink from '../../common/CustomLink/CustomLink';
import { Icon } from '../../common/Icon/Icon';
import Markdown from '../../common/Markdown/Markdown';
import NoTagRenderer from '../../common/NoTagRenderer/NoTagRenderer';
import { TBackground, addBackground, slugify } from '../../core/helpers';
import useFeedback from '../../core/hooks/useFeedback';
import useModal from '../../core/hooks/useModal';
import { ILink } from '../../core/models';
import Chat, { IOpenAI } from './Chat/Chat';
import './OpenAI.css';

interface IProps {
  title: string;
  desc?: string;
  cta?: ILink;
  openai?: IOpenAI;
  background?: TBackground;
}

function OpenAI({ title, desc, cta, openai, background }: IProps) {
  const btnRef = useRef(null);
  const {
    closeModal,
    startTimer,
    feedbackCookie,
    Modal: FeedbackModal,
  } = useFeedback({ ai: openai, btnRef });
  const closeCallback = useMemo(
    () => ({
      closeCallback: closeModal,
    }),
    [closeModal],
  );
  const { modal, open, close } = useModal(closeCallback);

  const openModal = useCallback(() => {
    if (!btnRef.current) return;
    open(btnRef.current);
    if (!feedbackCookie) startTimer();
  }, [feedbackCookie, open, startTimer]);

  return (
    <section
      className={`openai${background ? ' section-bg' : ' section'}`}
      {...addBackground({ img: undefined, gradient: background })}
    >
      <div className="wrap">
        <div className="openai__container gap-lg">
          <div className="openai__left">
            <h2 className="openai__title h2">
              <Markdown
                components={{
                  p: NoTagRenderer,
                }}
              >
                {title}
              </Markdown>
            </h2>
          </div>
          <div className="openai__right gap-md">
            {!desc ? null : <Markdown className="openai__desc markdown h3">{desc}</Markdown>}
            {!cta && !openai?.assistantID ? null : (
              <div className="openai__btn-container">
                {!cta ? null : <CustomLink className="openai__cta" link={cta} />}
                {!openai?.assistantID ? null : (
                  <CustomButton
                    className="btn"
                    onClick={openModal}
                    type="button"
                    ref={btnRef}
                    id={openai?.btnId}
                  >
                    {openai.btnLabel || 'Open'}
                  </CustomButton>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!openai?.assistantID
        ? null
        : modal({
            side: 'right',
            opaque: true,
            keepMounted: true,
            children: (
              <>
                <CustomButton
                  className="modal__close"
                  onClick={close}
                  type="button"
                  id={`${slugify(title)}-modal-close`}
                >
                  <Icon.Plus className="icon-large" />
                  <span className="sr-only">Close modal</span>
                </CustomButton>
                {!openai?.assistantID ? null : <Chat ai={openai} />}
              </>
            ),
          })}
      <FeedbackModal />
    </section>
  );
}

export default OpenAI;
